import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { rebuiltCreatorAPI } from '../services/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { rebuiltCreatorProviderStackOptionsForRole } from '../lib/rebuiltCreatorProviderStacks';

const STATUS_SCHEMA = 'agent_creator_application_job_status_v1';
const CLEAN_STATUS = 'lifecycle.clean_save_ready';
const REVIEW_STATUS = 'lifecycle.review_required';
const CANCELLED_STATUS = 'lifecycle.cancelled';
const RELOAD_STATUS = 'lifecycle.reload_requested';

const DEFAULT_FORM = {
  name: '',
  description: '',
  script: '',
  knowledgeBase: '',
  customInstructions: '',
  prospectContext: '{}',
  toolBindings: '[]',
  workflowProbeInputs: '{}',
  builderStack: 'hermes_gpt',
  runtimeStack: 'hermes_gpt',
  latencyStack: 'hermes_gpt',
  voice: 'Rachel',
  language: 'English',
};

export const centralCreatorJobIsClean = (status) => (
  status?.schema === STATUS_SCHEMA
  && status?.status === CLEAN_STATUS
  && status?.clean === true
  && status?.deployable === true
  && status?.callable === true
  && status?.persistence_permit_granted === true
  && Boolean(status?.agent_id)
);

const lifecycleLabel = (status) => {
  if (!status) return 'Ready';
  if (centralCreatorJobIsClean(status)) return 'Saved';
  if (status.status === REVIEW_STATUS) return 'Review required';
  if (status.status === CANCELLED_STATUS) return 'Cancelled';
  if (status.status === RELOAD_STATUS) return 'Reload requested';
  if (status.status === 'lifecycle.paused') return 'Paused';
  if (status.status === 'lifecycle.drafting_ready') return 'Building';
  if (status.status === 'lifecycle.resume_revalidation_required') {
    return 'Revalidating';
  }
  return String(status.status || 'Building')
    .replace('lifecycle.', '')
    .replaceAll('_', ' ');
};

const RebuiltAgentCreatorModal = ({
  open,
  onOpenChange,
  onSaved,
}) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [controlPending, setControlPending] = useState('');
  const [error, setError] = useState('');
  const savedAgentRef = useRef('');

  const clean = centralCreatorJobIsClean(jobStatus);
  const terminal = clean || [
    REVIEW_STATUS,
    CANCELLED_STATUS,
    RELOAD_STATUS,
  ].includes(jobStatus?.status);
  const running = Boolean(jobId) && !terminal;

  const progress = useMemo(() => {
    if (clean) return 100;
    if (!jobStatus) return 0;
    if (jobStatus.status === REVIEW_STATUS) return 100;
    const count = Number(jobStatus.event_count || 1);
    return Math.min(92, 12 + (count * 7));
  }, [clean, jobStatus]);

  useEffect(() => {
    if (!jobId || terminal) return undefined;
    let stopped = false;
    let timer;

    const poll = async () => {
      try {
        const response = await rebuiltCreatorAPI.getJob(jobId);
        if (stopped) return;
        setJobStatus(response.data);
        setError('');
        timer = window.setTimeout(poll, 1500);
      } catch (requestError) {
        if (stopped) return;
        setError(
          requestError?.response?.data?.detail
          || requestError?.message
          || 'Unable to read creator status.',
        );
        timer = window.setTimeout(poll, 3000);
      }
    };

    poll();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [jobId, terminal]);

  useEffect(() => {
    if (
      !clean
      || !jobStatus?.agent_id
      || savedAgentRef.current === jobStatus.agent_id
    ) {
      return;
    }
    savedAgentRef.current = jobStatus.agent_id;
    onSaved?.(jobStatus.agent_id);
  }, [clean, jobStatus, onSaved]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const reset = () => {
    setForm(DEFAULT_FORM);
    setJobId('');
    setJobStatus(null);
    setSubmitting(false);
    setControlPending('');
    setError('');
    savedAgentRef.current = '';
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !running) reset();
    onOpenChange(nextOpen);
  };

  const readScriptFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      updateField('script', await file.text());
      if (!form.name) updateField('name', file.name.replace(/\.[^.]+$/, ''));
      setError('');
    } catch (fileError) {
      setError(fileError?.message || 'Unable to read the selected file.');
    }
  };

  const launch = async () => {
    setSubmitting(true);
    setError('');
    try {
      const prospectContext = JSON.parse(form.prospectContext || '{}');
      const applicationToolBindings = JSON.parse(form.toolBindings || '[]');
      const workflowProbeInputs = JSON.parse(
        form.workflowProbeInputs || '{}',
      );
      if (
        !prospectContext
        || Array.isArray(prospectContext)
        || typeof prospectContext !== 'object'
      ) {
        throw new Error('Prospect context must be a JSON object.');
      }
      if (!Array.isArray(applicationToolBindings)) {
        throw new Error('Tool bindings must be a JSON array.');
      }
      if (
        !workflowProbeInputs
        || Array.isArray(workflowProbeInputs)
        || typeof workflowProbeInputs !== 'object'
      ) {
        throw new Error('Workflow probe inputs must be a JSON object.');
      }
      const bindingIds = applicationToolBindings
        .map((binding) => binding?.tool_binding_id)
        .filter(Boolean)
        .sort();
      const probeBindingIds = Object.keys(workflowProbeInputs).sort();
      if (
        bindingIds.length !== probeBindingIds.length
        || bindingIds.some(
          (bindingId, index) => bindingId !== probeBindingIds[index],
        )
      ) {
        throw new Error(
          'Workflow probe inputs must match the declared tool binding IDs.',
        );
      }
      const executionOptions = {
        max_repair_cycles: 64,
        max_latency_optimization_cycles: 0,
        hermes_timeout_seconds: 240,
      };
      if (applicationToolBindings.length > 0) {
        Object.assign(executionOptions, {
          workflow_proof_mode: 'application_runtime',
          workflow_probe_inputs: workflowProbeInputs,
        });
      }
      const response = await rebuiltCreatorAPI.createJob({
        authoring_mode: 'source_bundle',
        sources: {
          script: form.script,
          knowledge_base: form.knowledgeBase
            .split('\n')
            .map((row) => row.trim())
            .filter(Boolean),
          custom_instructions: form.customInstructions,
          prospect_context: prospectContext,
          application_tool_bindings: applicationToolBindings,
        },
        provider_selection: {
          builder_stack: form.builderStack,
          runtime_stack: form.runtimeStack,
          latency_stack: form.latencyStack,
          ttft_threshold_ms: 100000,
        },
        execution_options: executionOptions,
        agent: {
          name: form.name,
          description: form.description,
          voice: form.voice,
          language: form.language,
        },
      });
      if (response.data?.schema !== STATUS_SCHEMA || !response.data?.job_id) {
        throw new Error('Creator returned an invalid lifecycle response.');
      }
      setJobId(response.data.job_id);
      setJobStatus(response.data);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail
        || requestError?.message
        || 'Unable to start the rebuilt creator.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendControl = async (action) => {
    if (!jobId) return;
    setControlPending(action);
    setError('');
    try {
      const response = await rebuiltCreatorAPI.controlJob(jobId, action);
      if (response.data?.schema !== STATUS_SCHEMA) {
        throw new Error('Creator returned an invalid control response.');
      }
      setJobStatus(response.data);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail
        || requestError?.message
        || `Unable to ${action} the creator.`,
      );
    } finally {
      setControlPending('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-hidden border-gray-200 bg-white p-0">
        <DialogHeader className="border-b border-gray-200 px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Create Agent
          </DialogTitle>
        </DialogHeader>

        {!jobId ? (
          <Tabs defaultValue="source" className="min-h-0">
            <TabsList className="mx-6 mt-4 grid w-[calc(100%-3rem)] grid-cols-2">
              <TabsTrigger value="source">Source</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            <div className="max-h-[62vh] overflow-y-auto px-6 pb-2">
              <TabsContent value="source" className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-name">Name</Label>
                    <Input
                      id="rebuilt-agent-name"
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-description">Description</Label>
                    <Input
                      id="rebuilt-agent-description"
                      value={form.description}
                      onChange={(event) => updateField('description', event.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="rebuilt-agent-script">Script</Label>
                    <Label
                      htmlFor="rebuilt-agent-script-file"
                      className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-700"
                    >
                      <FileText className="h-4 w-4" />
                      Import
                    </Label>
                    <Input
                      id="rebuilt-agent-script-file"
                      type="file"
                      accept=".txt,.md,.json"
                      className="hidden"
                      onChange={readScriptFile}
                    />
                  </div>
                  <Textarea
                    id="rebuilt-agent-script"
                    className="min-h-48 resize-y font-mono text-sm"
                    value={form.script}
                    onChange={(event) => updateField('script', event.target.value)}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-kb">Knowledge base</Label>
                    <Textarea
                      id="rebuilt-agent-kb"
                      className="min-h-28 resize-y"
                      value={form.knowledgeBase}
                      onChange={(event) => updateField('knowledgeBase', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-instructions">Custom instructions</Label>
                    <Textarea
                      id="rebuilt-agent-instructions"
                      className="min-h-28 resize-y"
                      value={form.customInstructions}
                      onChange={(event) => updateField('customInstructions', event.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="settings" className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  {[
                    ['builderStack', 'Builder'],
                    ['runtimeStack', 'Runtime'],
                    ['latencyStack', 'Latency'],
                  ].map(([field, label]) => (
                    <div className="space-y-2" key={field}>
                      <Label>{label}</Label>
                      <Select
                        value={form[field]}
                        onValueChange={(value) => updateField(field, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {rebuiltCreatorProviderStackOptionsForRole(field).map(({ value, label: text }) => (
                            <SelectItem key={value} value={value}>
                              {text}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-voice">Voice</Label>
                    <Input
                      id="rebuilt-agent-voice"
                      value={form.voice}
                      onChange={(event) => updateField('voice', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-language">Language</Label>
                    <Input
                      id="rebuilt-agent-language"
                      value={form.language}
                      onChange={(event) => updateField('language', event.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-context">Prospect context</Label>
                    <Textarea
                      id="rebuilt-agent-context"
                      className="min-h-36 resize-y font-mono text-sm"
                      value={form.prospectContext}
                      onChange={(event) => updateField('prospectContext', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-tools">Tool bindings</Label>
                    <Textarea
                      id="rebuilt-agent-tools"
                      className="min-h-36 resize-y font-mono text-sm"
                      value={form.toolBindings}
                      onChange={(event) => updateField('toolBindings', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rebuilt-agent-workflow-probes">
                      Workflow probe inputs
                    </Label>
                    <Textarea
                      id="rebuilt-agent-workflow-probes"
                      className="min-h-36 resize-y font-mono text-sm"
                      value={form.workflowProbeInputs}
                      onChange={(event) => updateField(
                        'workflowProbeInputs',
                        event.target.value,
                      )}
                    />
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className="space-y-6 px-6 py-8">
            <div className="flex items-start gap-4">
              {clean ? (
                <CheckCircle2 className="mt-0.5 h-6 w-6 text-green-600" />
              ) : jobStatus?.status === REVIEW_STATUS ? (
                <AlertTriangle className="mt-0.5 h-6 w-6 text-amber-600" />
              ) : jobStatus?.status === CANCELLED_STATUS ? (
                <XCircle className="mt-0.5 h-6 w-6 text-red-600" />
              ) : (
                <Loader2 className="mt-0.5 h-6 w-6 animate-spin text-blue-600" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-gray-900">
                  {lifecycleLabel(jobStatus)}
                </div>
                <div className="mt-1 break-words text-sm text-gray-500">
                  {jobStatus?.execution_failure?.failure_message
                    || jobStatus?.reason
                    || jobStatus?.phase
                    || jobId}
                </div>
              </div>
            </div>
            <Progress value={progress} className="h-2" />

            {running && (
              <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={Boolean(controlPending)}
                  onClick={() => sendControl('pause')}
                >
                  <Pause className="mr-2 h-4 w-4" />
                  Pause
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={Boolean(controlPending)}
                  onClick={() => sendControl('resume')}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Resume
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={Boolean(controlPending)}
                  onClick={() => sendControl('reload')}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reload
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={Boolean(controlPending)}
                  onClick={() => sendControl('cancel')}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mx-6 mb-2 flex items-start gap-2 border-l-2 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}

        <DialogFooter className="border-t border-gray-200 px-6 py-4">
          {!jobId ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={
                  submitting
                  || !form.name.trim()
                  || !form.script.trim()
                }
                onClick={launch}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Build Agent
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              disabled={running}
              onClick={() => handleOpenChange(false)}
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RebuiltAgentCreatorModal;
